import {
  MENU_GROUP_TO_DOMAIN,
  PRODUCT_DOMAINS,
  getLeafMenuItems,
  type MenuItem,
} from '@/config/menuConfig'
import type { PermissionCode } from '@/types'
import type { RouteRecordRaw } from 'vue-router'

export interface RoutePermissionMeta {
  permissions?: readonly PermissionCode[]
  requiresAdmin?: boolean
  requiresAuth?: boolean
  menuGroup?: string
  requiresRole?: readonly string[] | string
}

export interface RoutePermissionTarget {
  path?: string
  name?: string | symbol | null | undefined
  meta?: RoutePermissionMeta
}

export interface RouteAuditEntry {
  path: string
  meta?: RoutePermissionMeta
}

const PUBLIC_ROUTE_NAMES = new Set(['login', 'PublicChat', 'forbidden'])
const ADMIN_ROUTE_PREFIX = '/control/'

function normalizePath(path: string, parentPath = ''): string {
  if (path.startsWith('/')) return path
  if (!parentPath || parentPath === '/') return `/${path}`.replace(/\/+$/, '') || '/'
  return `${parentPath.replace(/\/+$/, '')}/${path}`.replace(/\/+$/, '')
}

function routeNameKey(route: RoutePermissionTarget): string {
  if (typeof route.name === 'string' && route.name.trim()) {
    return route.name.trim()
  }
  return (route.path ?? 'route')
    .replace(/^\/+/, '')
    .replace(/[:/?#&=]+/g, '-')
    .replace(/\/+/g, '-')
    .replace(/^-|-$/g, '') || 'route'
}

function menuPermission(domainKey: string, menuKey: string): PermissionCode {
  return `menu.${domainKey}.${menuKey}`
}

function buildMenuPermissionIndex(): Map<string, readonly PermissionCode[]> {
  const index = new Map<string, PermissionCode[]>()
  for (const domain of PRODUCT_DOMAINS) {
    for (const item of getLeafMenuItems(domain.menu)) {
      if (!item.route) continue
      const current = index.get(item.route) ?? []
      current.push(menuPermission(domain.key, item.key))
      index.set(item.route, current)
    }
  }
  return index
}

const MENU_PERMISSION_BY_ROUTE = buildMenuPermissionIndex()

const LEGACY_PERMISSION_ALIASES: Record<PermissionCode, readonly PermissionCode[]> = {
  'menu.agent-workspace.agent-workspace': ['menu.agent.agent-workspace'],
  'menu.workbench.growth-strategy-list': ['menu.operations.growth-strategy-list'],
  'menu.workbench.competitor-materials': ['menu.operations.competitor-materials'],
  'menu.workbench.channel-management': ['menu.operations.channel-management'],
  'menu.data.datalake-catalog': ['menu.asset.datalake-catalog'],
  'menu.data.track-catalog': ['menu.asset.track-catalog'],
  'menu.data.datalake-datasets': ['menu.asset.datalake-datasets'],
  'menu.data.governance-tables-metadata': ['menu.asset.governance-tables-metadata'],
  'menu.data.datalake-overview': ['menu.governance.datalake-overview'],
  'menu.agent.governance-knowledge-base': ['menu.knowledge.governance-knowledge-base'],
  'menu.agent.governance-metrics': ['menu.knowledge.governance-metrics'],
  'menu.agent.governance-business-terms': ['menu.knowledge.governance-business-terms'],
  'menu.agent.governance-subject-tree': ['menu.knowledge.governance-subject-tree'],
  'menu.agent.governance-dimensions': ['menu.knowledge.governance-dimensions'],
  'menu.agent.governance-report-templates': ['menu.knowledge.governance-report-templates'],
  'menu.agent.governance-sql-examples': ['menu.knowledge.governance-sql-examples'],
  'menu.agent.governance-instructions': ['menu.knowledge.governance-instructions'],
}

function withLegacyPermissionAliases(permissions: readonly PermissionCode[]): readonly PermissionCode[] {
  const expanded = new Set<PermissionCode>(permissions)
  for (const code of permissions) {
    for (const alias of LEGACY_PERMISSION_ALIASES[code] ?? []) {
      expanded.add(alias)
    }
  }
  return Array.from(expanded)
}

function findInheritedMenuPermissions(path: string): readonly PermissionCode[] {
  let best: readonly PermissionCode[] = []
  let bestLength = -1
  for (const [routePath, permissions] of MENU_PERMISSION_BY_ROUTE) {
    if (path === routePath || path.startsWith(`${routePath}/`)) {
      if (routePath.length > bestLength) {
        best = permissions
        bestLength = routePath.length
      }
    }
  }
  return best
}

function fallbackPermission(route: RoutePermissionTarget, path: string): readonly PermissionCode[] {
  if (path === '/dashboard-generator') return ['menu.agent.dashboard-generator']
  if (route.meta?.requiresAdmin || path.startsWith(ADMIN_ROUTE_PREFIX)) {
    return [menuPermission('admin', routeNameKey(route))]
  }
  if (route.meta?.menuGroup) {
    const domainKey = MENU_GROUP_TO_DOMAIN[route.meta.menuGroup] ?? route.meta.menuGroup
    return [menuPermission(domainKey, routeNameKey(route))]
  }
  return []
}

export function routePermissionsFor(route: RoutePermissionTarget, path = route.path ?? ''): readonly PermissionCode[] {
  if (route.name && PUBLIC_ROUTE_NAMES.has(String(route.name))) return []

  const direct = MENU_PERMISSION_BY_ROUTE.get(path)
  if (direct) return withLegacyPermissionAliases(direct)

  const inherited = findInheritedMenuPermissions(path)
  if (inherited.length > 0) return withLegacyPermissionAliases(inherited)

  return withLegacyPermissionAliases(fallbackPermission(route, path))
}

function shouldRequireAuth(route: RoutePermissionTarget, permissions: readonly PermissionCode[]): boolean {
  if (route.name && PUBLIC_ROUTE_NAMES.has(String(route.name))) return false
  if (route.meta?.requiresAuth === false) return false
  return permissions.length > 0 || route.meta?.requiresAdmin === true
}

export function applyRoutePermissions(routeRecords: readonly RouteRecordRaw[], parentPath = ''): void {
  for (const route of routeRecords) {
    const path = normalizePath(route.path, parentPath)
    const meta = (route.meta ?? {}) as RoutePermissionMeta
    const permissions = meta.permissions ?? routePermissionsFor({ path, name: route.name, meta }, path)
    route.meta = {
      ...meta,
      requiresAuth: meta.requiresAuth ?? shouldRequireAuth({ path, name: route.name, meta }, permissions),
      permissions,
    }

    if (route.children) {
      applyRoutePermissions(route.children, path)
    }
  }
}

export function flattenRoutesForPermissionAudit(
  routeRecords: readonly RouteRecordRaw[],
  parentPath = '',
): RouteAuditEntry[] {
  const entries: RouteAuditEntry[] = []
  for (const route of routeRecords) {
    const path = normalizePath(route.path, parentPath)
    entries.push({ path, meta: route.meta as RoutePermissionMeta | undefined })
    if (route.children) {
      entries.push(...flattenRoutesForPermissionAudit(route.children, path))
    }
  }
  return entries
}

export function requiredPermissionsText(route: RoutePermissionTarget): string {
  const required = route.meta?.permissions ?? []
  if (required.length > 0) return required.join(',')
  if (route.meta?.requiresAdmin) return 'admin'
  return ''
}

export function menuItemRouteTarget(item: MenuItem): RoutePermissionTarget {
  const path = item.route
  if (!path) return { name: item.key, meta: { permissions: [] } }
  return {
    path,
    name: item.key,
    meta: {
      permissions: routePermissionsFor({ path, name: item.key, meta: {} }, path),
    },
  }
}

export function filterMenuTreeByAccess(
  items: readonly MenuItem[],
  canAccessRoute: (item: RoutePermissionTarget) => boolean,
): MenuItem[] {
  const visibleItems: MenuItem[] = []

  for (const item of items) {
    const children = item.children
      ? filterMenuTreeByAccess(item.children, canAccessRoute)
      : undefined
    const selfVisible = item.route ? canAccessRoute(menuItemRouteTarget(item)) : false

    if (selfVisible || (children && children.length > 0)) {
      visibleItems.push({
        ...item,
        ...(children ? { children } : {}),
      })
    }
  }

  return visibleItems
}
